import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Title3,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ArrowLeft20Regular } from '@fluentui/react-icons';
import { useUsers } from '../../hooks/useUsers';
import { UserFull } from '../../types/user';
import { ApiErrorInfo } from '../../types/api';
import UserForm from './components/UserForm';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  backButton: {
    minWidth: 'auto',
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    padding: tokens.spacingHorizontalXXL,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
});

const UserFormPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('id');
  
  const [editingUser, setEditingUser] = React.useState<UserFull | undefined>();
  const [wasSaving, setWasSaving] = React.useState(false);
  const [currentError, setCurrentError] = React.useState<ApiErrorInfo | null>(null);
  const [isLoadingUser, setIsLoadingUser] = React.useState(!!userId);
  const [userNotFound, setUserNotFound] = React.useState(false);

  const {
    users,
    saveLoading,
    saveError,
    userLoading,
    fetchUsers,
    saveUser,
  } = useUsers();

  // Sincronizar erro atual com saveError
  React.useEffect(() => {
    if (saveError) {
      setCurrentError(saveError);
    }
  }, [saveError]);

  // Carregar lista de usuários e buscar usuário específico se for edição
  React.useEffect(() => {
    if (!userLoading) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]);

  // Buscar usuário específico quando a lista for carregada
  React.useEffect(() => {
    if (userId && users && users.length > 0) {
      const user = users.find(u => u.id === userId);
      if (user) {
        // Garantir que telefones seja sempre um array
        const userWithPhones = {
          ...user,
          telefones: user.telefones || []
        };
        setEditingUser(userWithPhones);
        setUserNotFound(false);
      } else {
        console.warn('Usuário não encontrado na lista:', userId);
        setUserNotFound(true);
      }
      setIsLoadingUser(false);
    } else if (!userId) {
      setIsLoadingUser(false);
      setUserNotFound(false);
    } else if (userId && users && users.length === 0) {
      // Lista carregada mas vazia ou ainda carregando
      setIsLoadingUser(false);
    }
  }, [userId, users]);

  const handleSaveUser = async (userData: UserFull) => {
    setWasSaving(true);
    setCurrentError(null);
    try {
      await saveUser(userData);
      // Sucesso será tratado pelo useEffect abaixo
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      setWasSaving(false);
    }
  };

  // Navegar de volta quando salvar com sucesso
  React.useEffect(() => {
    if (wasSaving && !saveLoading) {
      if (!saveError) {
        navigate('/users');
      } else {
        setWasSaving(false);
      }
    }
  }, [wasSaving, saveLoading, saveError, navigate]);

  const handleCancel = () => {
    navigate('/users');
  };

  const handleBack = () => {
    navigate('/users');
  };

  if (isLoadingUser || userLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner label="Carregando usuário..." size="large" />
        </div>
      </div>
    );
  }

  if (userNotFound) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular />}
            onClick={handleBack}
            className={styles.backButton}
          />
          <Title3 className={styles.title}>Usuário não encontrado</Title3>
        </div>
        <div className={styles.content}>
          <p>O usuário solicitado não foi encontrado.</p>
          <Button onClick={handleBack}>Voltar para lista</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={handleBack}
          className={styles.backButton}
        />
        <Title3 className={styles.title}>
          {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        </Title3>
      </div>

      <div className={styles.content}>
        <UserForm
          user={editingUser}
          onSave={handleSaveUser}
          onCancel={handleCancel}
          loading={saveLoading}
          error={currentError}
        />
      </div>
    </div>
  );
};

export default UserFormPage;
